import {AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {NgbNavModule} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {Subscription} from 'rxjs';
import {BreadcrumbService} from '@cdss/services/breadcrumb.service';
import {BreadcrumbItem} from '@cdss/services/breadcrumb-item.value';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {OrganisationsService} from '@shared/api/services/v2/organisations.service';
import {isAlreadyNotified} from '@shared/api/services/v2/api-request';
import {
    isOrganisationActive,
    isOrganisationDeactivated,
    isOrganisationDeleted,
    OrganisationDetail,
    OrganisationLinkedContract,
    OrganisationLinkedService
} from '@shared/api/models/query/organisation.model';
import {OrganisationUpdate} from '@shared/api/models/command/organisation.model';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {AuthService} from '@shared/services/security/auth.service';
import {ORGANISATIONS_LIST_ROUTE} from '@shared/services/router/organisation-routes';
import {
    ORGANISATION_CODE_PREFIX,
    OrganisationFormComponent,
    OrganisationFormValue
} from '../form/organisation-form.component';
import {OrganisationDeletePanelComponent} from './organisation-delete-panel.component';
import {OrganisationContractsTabComponent} from './tabs/organisation-contracts-tab.component';
import {OrganisationCareProgramsTabComponent} from './tabs/organisation-care-programs-tab.component';
import {OrganisationServicesTabComponent} from './tabs/organisation-services-tab.component';
import {OrganisationAdminsTabComponent} from './tabs/organisation-admins-tab.component';
import {OrganisationHistoryTabComponent} from './tabs/organisation-history-tab.component';

/** Error codes the API sends in the body of a 422. */
const DUPLICATE_CODE = 'organisation_code_already_exists';
const READ_ONLY = 'organisation_read_only';

export type OrganisationLoadError = 'denied' | 'not-found' | 'error';
export type OrganisationTab = 'general' | 'contracts' | 'care-programmes' | 'services' | 'admins' | 'history';

/**
 * UNIVAPLAT-3693: organisation detail page (route `cdss/admin/organisations/:id`, VIEW_ORGANISATIONS)
 * with the tab shell and the General details tab. The Contracts (UNIVAPLAT-3695), Care programmes (UNIVAPLAT-3696),
 * Services (UNIVAPLAT-3697), Organisation admins (UNIVAPLAT-3698) and History (UNIVAPLAT-3699) tabs are plugged in.
 * The History tab emits `changed` after a deactivate / reactivate and the page reloads the organisation.
 *
 * The admins and the linked lists answer 422 on a deactivated or deleted organisation, so they are
 * loaded only while it is active. The backend owns error texts. Staff names, emails and reasons are
 * personal data: never logged, dropped on destroy.
 */
@Component({
    selector: 'cdss-organisation-detail-page',
    standalone: true,
    imports: [
        RouterLink,
        NgbNavModule,
        SpinnerComponent,
        OrganisationFormComponent,
        OrganisationDeletePanelComponent,
        OrganisationContractsTabComponent,
        OrganisationCareProgramsTabComponent,
        OrganisationServicesTabComponent,
        OrganisationAdminsTabComponent,
        OrganisationHistoryTabComponent,
    ],
    templateUrl: './organisation-detail.page.html',
    styleUrls: ['./organisation-detail.page.scss']
})
export class OrganisationDetailPage implements OnInit, OnDestroy, AfterViewChecked {
    readonly listRoute = '/' + ORGANISATIONS_LIST_ROUTE;

    organisation: OrganisationDetail | null = null;
    loading = true;
    loadError: OrganisationLoadError | null = null;
    errorMessage = '';
    activeTab: OrganisationTab = 'general';

    saving = false;
    codeError: string | null = null;

    /** Linked contracts and services: drive the delete panel and the Deactivate button. */
    linkedContracts: OrganisationLinkedContract[] = [];
    linkedServices: OrganisationLinkedService[] = [];

    private organisationId: number | null = null;
    private routeSub?: Subscription;
    private focusMessage = false;
    private linksRequest = 0;
    private loadRequest = 0;

    @ViewChild('message') private messageEl?: ElementRef<HTMLElement>;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private breadcrumbService: BreadcrumbService,
        private organisationsService: OrganisationsService,
        private authService: AuthService,
        private toastr: ToastrService,
    ) {
    }

    get isEditable(): boolean {
        return !!this.organisation && isOrganisationActive(this.organisation);
    }

    /** `ORG-` stays fixed in front of the code; codes that do not start with it stay one free input. */
    get codePrefix(): string | null {
        return this.organisation?.code?.startsWith(ORGANISATION_CODE_PREFIX) ? ORGANISATION_CODE_PREFIX : null;
    }

    get canEditCode(): boolean {
        return this.hasPermission(Permissions.EDIT_ORGANISATION_CODE);
    }

    get canManageAdmins(): boolean {
        return this.hasPermission(Permissions.MANAGE_ORGANISATION_ADMINS);
    }

    get canViewHistory(): boolean {
        return this.hasPermission(Permissions.VIEW_ORGANISATION_HISTORY);
    }

    get canDelete(): boolean {
        return !!this.organisation && !isOrganisationDeleted(this.organisation) && this.hasPermission(Permissions.DELETE_ORGANISATION);
    }

    /** Holders of these permissions need the linked lists (delete panel, Deactivate button). */
    get needsLinks(): boolean {
        return this.hasPermission(Permissions.DELETE_ORGANISATION) || this.hasPermission(Permissions.CHANGE_ORGANISATION_STATE);
    }

    get statusLabel(): string | null {
        if (!this.organisation) {
            return null;
        }
        if (isOrganisationDeleted(this.organisation)) {
            return 'Deleted';
        }
        return isOrganisationDeactivated(this.organisation) ? 'Deactivated' : null;
    }

    ngOnInit(): void {
        this.setBreadcrumb(null);
        this.routeSub = this.route.paramMap.subscribe(params => {
            const id = Number(params.get('id'));
            this.organisationId = Number.isInteger(id) && id > 0 ? id : null;
            this.activeTab = 'general';
            void this.load(true);
        });
    }

    ngOnDestroy(): void {
        this.routeSub?.unsubscribe();
        // Drop personal data (names, addresses, linked records).
        this.organisation = null;
        this.linkedContracts = [];
        this.linkedServices = [];
    }

    /** Moves focus to the denied / not-found message once it is rendered. */
    ngAfterViewChecked(): void {
        if (this.focusMessage && this.messageEl) {
            this.focusMessage = false;
            this.messageEl.nativeElement.focus();
        }
    }

    async load(showSpinner = false): Promise<void> {
        if (this.organisationId === null) {
            this.showLoadError(404);
            return;
        }
        const request = ++this.loadRequest;
        if (showSpinner) {
            this.loading = true;
            this.organisation = null;
            this.loadError = null;
            this.linkedContracts = [];
            this.linkedServices = [];
        }
        try {
            const organisation = await this.organisationsService.get(this.organisationId);
            if (request !== this.loadRequest) {
                return;
            }
            this.organisation = organisation;
            this.loadError = null;
            this.setBreadcrumb(organisation.name ?? organisation.code);
            await this.reloadLinks();
        } catch (error: any) {
            if (request !== this.loadRequest) {
                return;
            }
            // Never keep stale data on screen after a failed load.
            this.showLoadError(error?.status);
        } finally {
            if (request === this.loadRequest) {
                this.loading = false;
            }
        }
    }

    /** The History tab changed the organisation state: reload it (badge, read-only form, links). */
    async onStateChanged(): Promise<void> {
        await this.load();
    }

    /**
     * Refreshes `linkedContracts` and `linkedServices`; the Contracts and Services tabs call it through
     * their `changed` output. Skipped (lists emptied) while the organisation is inactive, where the API
     * answers 422, or when the role has no use for them.
     */
    async reloadLinks(): Promise<void> {
        const organisation = this.organisation;
        if (!organisation || !this.needsLinks || !isOrganisationActive(organisation)) {
            this.linkedContracts = [];
            this.linkedServices = [];
            return;
        }
        const request = ++this.linksRequest;
        try {
            const [contracts, services] = await Promise.all([
                this.organisationsService.listContracts(organisation.id),
                this.organisationsService.listServices(organisation.id),
            ]);
            if (request !== this.linksRequest) {
                return;
            }
            this.linkedContracts = contracts;
            this.linkedServices = services;
        } catch (error: any) {
            if (request !== this.linksRequest) {
                return;
            }
            // Unknown links must not look like "no links": the API still refuses a delete that has them.
            this.linkedContracts = [];
            this.linkedServices = [];
            if (!isAlreadyNotified(error)) {
                this.toastr.error(error?.error?.error || 'The linked contracts and services could not be loaded.');
            }
        }
    }

    async onSave(value: OrganisationFormValue): Promise<void> {
        if (this.saving || !this.organisation || !this.isEditable) {
            return;
        }
        this.saving = true;
        this.codeError = null;
        const body: OrganisationUpdate = {
            name: value.name,
            address: value.address,
            city: value.city,
            postcode: value.postcode,
        };
        // Only a user with EDIT_ORGANISATION_CODE sends the code; the API rejects it for organisation admins.
        if (this.canEditCode) {
            body.code = value.code;
        }
        try {
            await this.organisationsService.update(this.organisation.id, body);
            this.toastr.success('Organisation updated', 'Success');
            await this.load();
        } catch (error: any) {
            const message: string | undefined = error?.error?.error;
            if (this.isErrorCode(error, DUPLICATE_CODE)) {
                this.codeError = message || 'This organisation code already exists.';
            } else {
                if (!isAlreadyNotified(error)) {
                    this.toastr.error(message || 'Something went wrong. Please try again.');
                }
                if (this.isErrorCode(error, READ_ONLY)) {
                    // The organisation was deactivated or deleted meanwhile: refresh so the form turns read-only.
                    await this.load();
                }
            }
        } finally {
            this.saving = false;
        }
    }

    /** The organisation was deleted: the detail page no longer applies, so go back to the list. */
    async onDeleted(): Promise<void> {
        await this.router.navigateByUrl(this.listRoute);
    }

    private showLoadError(status: number | undefined): void {
        this.organisation = null;
        this.linkedContracts = [];
        this.linkedServices = [];
        this.loadError = status === 403 ? 'denied' : (status === 404 ? 'not-found' : 'error');
        this.errorMessage = this.loadError === 'denied'
            ? 'You do not have access to this organisation.'
            : (this.loadError === 'not-found'
                ? 'This organisation does not exist.'
                : 'The organisation could not be loaded. Try again later.');
        this.focusMessage = true;
        this.loading = false;
    }

    private isErrorCode(error: any, code: string): boolean {
        const body = error?.error;
        return error?.status === 422 && [body?.code, body?.error, body?.message, body?.detail]
            .some(field => typeof field === 'string' && field.includes(code));
    }

    private hasPermission(permission: Permissions): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[permission] ?? []).some(role => userRoles.includes(role));
    }

    private setBreadcrumb(title: string | null): void {
        const items: BreadcrumbItem[] = [{title: 'Organisations', url: this.listRoute}];
        if (title) {
            items.push({title, url: null as unknown as string});
        }
        this.breadcrumbService.set(items);
    }
}
